import { SqliteSalarieRepository } from '@infra/persistence/SqliteSalarieRepository';
import { SqliteBulletinRepository } from '@infra/persistence/SqliteBulletinRepository';
import { SqliteEcritureRepository } from '@infra/persistence/SqliteEcritureRepository';
import { SqliteJsonRepository } from '@infra/persistence/SqliteJsonRepository';
import { gardeEcriturePeriodeCloturee } from '@infra/tenancy/ecriturePeriodeGuard';
import { TenantScopedRepository } from '@infra/tenancy/TenantScopedRepository';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Facture } from '@shared/types/Facture';
import type { CentreAnalytique } from '@shared/types/CentreAnalytique';
import type { IPersistencePort } from '@infra/persistence/IPersistencePort';
import { getSqlitePersistenceStatus } from '@infra/persistence/SqlitePersistenceBridge';

/** Persistance locale SQLite chiffrée — mode hors connexion après cutover. */
export class SqliteLocalRepositoriesAdapter implements IPersistencePort {
  readonly dataMode = 'LOCAL_OFFLINE' as const;
  readonly salaries: TenantScopedRepository<Salarie>;
  readonly bulletins: TenantScopedRepository<Bulletin>;
  readonly ecritures: TenantScopedRepository<Ecriture>;
  readonly factures: TenantScopedRepository<Facture>;
  readonly centresAnalytiques: TenantScopedRepository<CentreAnalytique>;

  constructor() {
    this.salaries = new TenantScopedRepository(new SqliteSalarieRepository(), 'salaries');
    this.bulletins = new TenantScopedRepository(new SqliteBulletinRepository(), 'bulletins');
    this.ecritures = new TenantScopedRepository(
      new SqliteEcritureRepository(),
      'ecritures',
      gardeEcriturePeriodeCloturee,
    );
    this.factures = new TenantScopedRepository(
      new SqliteJsonRepository<Facture>('factures'),
      'factures',
    );
    this.centresAnalytiques = new TenantScopedRepository(
      new SqliteJsonRepository<CentreAnalytique>('centres_analytiques'),
      'centres-analytiques',
    );
  }

  isRemoteOnline(): boolean {
    return true;
  }

  describe() {
    return {
      dataMode: this.dataMode,
      engine: 'sqlite' as const,
      dexiePrimary: false,
      sqliteAvailable: true,
    };
  }

  static async isActive(): Promise<boolean> {
    const status = await getSqlitePersistenceStatus();
    return Boolean(status.available && status.dexiePrimary === false);
  }
}
